<template>
  <div class="insights-ai-usage-models-widget insights-widget">
    <div class="widget-wrapper">
      <div class="widget-common widget-small widget-full-height">
        <div class="widget-title d-flex align-items-baseline">
          Where the cost lands
          <i
            v-tooltip="tooltipOptions"
            class="hi hi-info-filled ml-5"
            :title="infoTooltip"
          />
        </div>
        <div
          class="widget-body"
          :class="bodyClasses"
        >
          <template v-if="isReady">
            <InsightsNoData
              v-if="isEmptyWidget"
              :type="emptyReason"
            />
            <div v-else>
              <AppChartLineStacked
                :values="segments"
                :has-axis-legend="false"
                has-tooltips
              />
              <div class="models-legend mt-15">
                <div
                  v-for="(model, index) in models"
                  :key="model.model"
                  class="d-flex align-items-center models-legend-row"
                >
                  <span
                    class="dot mr-5"
                    :style="{ backgroundColor: palette[index % palette.length] }"
                  />
                  <span class="model-name">{{ model.model }}</span>
                  <span class="ml-auto text-muted">
                    {{ number(model.io_tokens) }} tok · {{ number(model.events) }} events ·
                    <b>${{ model.cost_usd.toLocaleString() }}</b>
                  </span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { INSIGHTS } from '~/app_constants';
import { numericValue } from '~/utils/insights_utils';
import { Insights } from '~/mixins';
import { mapGetters } from 'vuex';
import AppChartLineStacked from '~/components/AppChartLineStacked.vue';

export const MODEL_PALETTE = [
  '#4468d8', '#17a36d', '#c7812b', '#d98ab0', '#bd4651', '#5a9fb5',
  '#7b5cd6', '#2aa7ff', '#f98080', '#6b7280', '#98be3d', '#eab308',
];

export default {
  name: 'InsightsAiUsageModelsWidget',
  components: {
    AppChartLineStacked,
    InsightsNoData: () => import('~/components/insights/InsightsNoData.vue'),
  },
  mixins: [Insights],
  data: () => ({
    widgetKey: INSIGHTS.api.aiUsage.models,
    palette: MODEL_PALETTE,
    tooltipOptions: Object.freeze({ html: true }),
    infoTooltip:
      'Estimated cost share by model. Claude cost is reported by the agent; OpenAI cost is derived ' +
      'from tokens at API rates. Benchmark values, not actual subscription spend.',
  }),
  computed: {
    ...mapGetters({ widgetData: 'aiUsageModels' }),
    models() {
      return this.widgetData?.models || [];
    },
    totalCost() {
      return this.models.reduce((sum, model) => sum + model.cost_usd, 0);
    },
    segments() {
      return this.models.map((model, index) => ({
        key: `m${index}`,
        value: this.totalCost ? +((model.cost_usd / this.totalCost) * 100).toFixed(2) : 0,
        tooltip: `${model.model} · $${model.cost_usd.toLocaleString()}`,
      }));
    },
  },
  methods: {
    number(value) {
      return numericValue(value || 0, { maxNumberBeforeSymbol: 9999, roundDigits: 1 });
    },
  },
};
</script>

<style lang="scss">
.insights-ai-usage-models-widget {
  $model-palette: #4468d8, #17a36d, #c7812b, #d98ab0, #bd4651, #5a9fb5,
    #7b5cd6, #2aa7ff, #f98080, #6b7280, #98be3d, #eab308;

  @for $i from 1 through length($model-palette) {
    .chart-line-stacked-canvas-m#{$i - 1} {
      background-color: nth($model-palette, $i);
    }
  }

  .dot {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .models-legend-row {
    font-size: 13px;
    padding: 3px 0;

    .model-name {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>
